import { Button, Tabs, TabsList, TabsTrigger } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { PageHeader } from '@/components/layout/page-header';
import { AllProgramsGrid } from '@/features/mm-programs/all-programs/all-programs-grid';
import { currentOf } from '@/features/mm-programs/all-programs/grid-rows';
import { useProgramDrafts } from '@/features/mm-programs/all-programs/use-program-drafts';
import { findUpcomingChanges, summariseDashboard } from '@/features/mm-programs/dashboard';
import { AttentionPanel, OverviewTiles, OverviewTilesSkeleton, UnconfiguredPanel, UpcomingChanges } from '@/features/mm-programs/dashboard-panels';
import { JobsView } from '@/features/mm-programs/jobs/jobs-view';
import { MarketOverview } from '@/features/mm-programs/market-overview/market-overview';
import { buildMmProgramColumns } from '@/features/mm-programs/mm-program-columns';
import { PickMarketMakerDialog } from '@/features/mm-programs/set-up-market-maker';
import { mmProgramQueryKey, useMmProgramDocuments, useMmPrograms, useSetUpMarketMaker } from '@/hooks/queries/mm-programs/use-mm-programs';
import { getErrorMessage } from '@/lib/api/error';
import { mmProgramPath } from '@/lib/paths';

const VIEWS = [
  { value: 'market-makers', label: 'Market makers' },
  { value: 'programs', label: 'All programs' },
  { value: 'markets', label: 'Market overview' },
  { value: 'jobs', label: 'Jobs' },
] as const;
type View = (typeof VIEWS)[number]['value'];
const DEFAULT_VIEW: View = 'market-makers';

const MmProgramsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = VIEWS.find((item) => item.value === searchParams.get('view'))?.value ?? DEFAULT_VIEW;
  const { data, isPending, isError, error, refetch } = useMmPrograms();
  const [isPicking, setIsPicking] = useState(false);

  // Full documents load once a view across all programs is opened; unsaved edits survive switching tabs.
  const pids = useMemo(() => (view === DEFAULT_VIEW || view === 'jobs' ? [] : (data?.marketMakers ?? []).map((marketMaker) => marketMaker.p)), [view, data]);
  const loaded = useMmProgramDocuments(pids);
  const drafts = useProgramDrafts(loaded.documents);
  const depthSources = useMemo(() => drafts.documents.map((document) => ({ pid: document.pid, marketMaker: currentOf(document) })), [drafts.documents]);
  const setView = (next: string) =>
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        if (next === DEFAULT_VIEW) params.delete('view');
        else params.set('view', next);
        return params;
      },
      { replace: true },
    );

  const setUpMarketMaker = useSetUpMarketMaker();
  const setUp = (pid: string) =>
    setUpMarketMaker.mutate(pid, {
      onSuccess: (response) => {
        setIsPicking(false);
        toast.success(`Set up ${response.marketMaker.name}. Add its schedules next.`);
        navigate(mmProgramPath(response.marketMaker.p));
      },
      onError: (error) => toast.error(`Could not set up the market maker: ${getErrorMessage(error)}`),
    });
  const pendingPid = setUpMarketMaker.isPending ? (setUpMarketMaker.variables ?? null) : null;

  const overview = useMemo(() => (data ? summariseDashboard(data) : null), [data]);
  const upcoming = useMemo(() => (data ? findUpcomingChanges(data) : []), [data]);
  const columns = useMemo(
    () => buildMmProgramColumns(data?.asOf ?? '', Math.max(0, ...(data?.marketMakers ?? []).map((marketMaker) => marketMaker.monthlyCompensationUsd))),
    [data],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        actions={
          data?.canEdit ? (
            <Button className="gap-1.5" onClick={() => setIsPicking(true)} type="button" variant="primary">
              <Plus className="size-4" />
              Set up market maker
            </Button>
          ) : null
        }
        documentTitle="MM Programs - Bespoke Ops Portal - One Trading"
        title="MM Programs"
      />

      {isError ? <QueryErrorBanner message={`Failed to fetch market makers: ${getErrorMessage(error)}`} onRetry={() => refetch()} /> : null}

      <Tabs onValueChange={setView} value={view}>
        <TabsList className="h-auto max-w-full flex-wrap">
          {VIEWS.map((item) => (
            <TabsTrigger key={item.value} value={item.value}>
              {item.label}
              {item.value === 'programs' && drafts.edited.length ? <span className="ml-1.5 size-2 rounded-full bg-blue-500" title="Unsaved edits" /> : null}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {view !== DEFAULT_VIEW && view !== 'jobs' && loaded.errors.length ? (
        <QueryErrorBanner
          message={`Failed to fetch ${loaded.errors.length} market maker document(s): ${getErrorMessage(loaded.errors[0])}`}
          onRetry={() => queryClient.refetchQueries({ queryKey: ['mm-programs', 'detail'] })}
        />
      ) : null}

      {view === 'programs' ? (
        <AllProgramsGrid drafts={drafts} isLoading={isPending || loaded.isPending} onReload={(pid) => queryClient.refetchQueries({ queryKey: mmProgramQueryKey(pid) })} />
      ) : null}

      {view === 'jobs' ? <JobsView marketMakers={data?.marketMakers ?? []} /> : null}

      {view === 'markets' ? <MarketOverview isLoading={isPending || loaded.isPending} sources={depthSources} /> : null}

      {view === DEFAULT_VIEW ? (
        <>
          {overview ? <AttentionPanel marketMakers={overview.needsAttention} /> : null}
          {overview && data ? <OverviewTiles overview={overview} /> : isPending ? <OverviewTilesSkeleton /> : null}

          <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_22rem]">
            <section aria-label="Market makers" className="min-w-0">
              <DataTable
                columns={columns}
                data={data?.marketMakers}
                defaultSorting={[{ id: 'monthlyCompensationUsd', desc: true }]}
                emptyMessage="No market makers have settings yet."
                isLoading={isPending}
                onRowClick={(marketMaker) => navigate(mmProgramPath(marketMaker.p))}
              />
            </section>
            {data ? (
              <aside className="grid content-start gap-6 lg:grid-cols-2 2xl:grid-cols-1">
                <UpcomingChanges changes={upcoming} />
                <UnconfiguredPanel accounts={data.unconfigured} accountsAvailable={data.accountsAvailable} canEdit={data.canEdit} onSetUp={setUp} pendingPid={pendingPid} />
              </aside>
            ) : null}
          </div>
        </>
      ) : null}

      {isPicking ? <PickMarketMakerDialog candidates={data?.unconfigured ?? []} onClose={() => setIsPicking(false)} onPick={setUp} pendingPid={pendingPid} /> : null}
    </div>
  );
};

export default MmProgramsPage;
